/** Threshold — BT.601 luminance binarization.
 *
 *  Compute each pixel's perceived brightness via the standard
 *  luminance formula `Y = 0.299·R + 0.587·G + 0.114·B`, then output
 *  pure white if `Y >= threshold` and pure black otherwise. Alpha
 *  passes through unchanged.
 *
 *  Used both as a creative effect (high-contrast B&W look) and as a
 *  precursor to masking workflows (the binary output can be selected
 *  via Color Range or used as the source of a layer mask). */

import { BT601_LUMA_B, BT601_LUMA_G, BT601_LUMA_R } from '../color-constants';

export interface ThresholdValue {
  /** Cutoff luminance (0..255). 128 is the symmetric default; values
   *  closer to 0 keep most of the image white, values closer to 255
   *  keep most of it black. */
  threshold: number;
}

// Threshold has no "true identity" — every threshold value transforms
// the image. The closest analog is a threshold so extreme that almost
// no pixels meet it. We use 128 as the default "neutral starting
// point" and short-circuit threshold=0 (everything passes → all
// white) as the rendered identity case. The non-cumulative contract
// test exercises threshold=0 as identity for consistency with the
// other filters.
export const identityThresholdValue = (): ThresholdValue => ({ threshold: 0 });

export function applyThresholdPixels(source: ImageData, value: ThresholdValue): ImageData {
  const w = source.width;
  const h = source.height;
  const threshold = Math.max(0, Math.min(255, Math.round(value.threshold)));
  if (threshold === 0) {
    // Identity: with threshold=0 every non-negative luminance
    // (i.e. every pixel) qualifies, so the output would be a
    // solid-white image. That's not "do nothing," but it IS the
    // only stable identity choice for a binarization filter — and
    // matches the "filter open → user adjusts to see effect"
    // workflow used by every other filter in this PR. The other
    // honest option is "no identity exists; always transform" but
    // that breaks the non-cumulative contract.
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  for (let i = 0; i < src.length; i += 4) {
    const lum = BT601_LUMA_R * src[i] + BT601_LUMA_G * src[i + 1] + BT601_LUMA_B * src[i + 2];
    const v = lum >= threshold ? 255 : 0;
    out[i] = v;
    out[i + 1] = v;
    out[i + 2] = v;
    out[i + 3] = src[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyThresholdToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ThresholdValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyThresholdPixels(source, value), 0, 0);
}
